
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, Radio, RefreshCw, X } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";

import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";
import ContentGrid from "../../../../components/grids/content/ContentGrid";

import StreamThumbnailCard from "../../../../components/cards/thumbnails/StreamThumbnailCard";
import SimplePlayer from "../../../../components/player/SimplePlayer";
import AmazonIVSPlayer from "../../../../components/streams/AmazonIVSPlayer";

import { useChannelStreams } from "../hooks/useChannelStreams";

import "./ChannelStreamsView.css";

const DEFAULT_LIMIT = 12;
const PREVIEW_LIMIT = 5;

const getStatus = (stream) =>
  String(stream?.status || "")
    .trim()
    .toLowerCase();

const getStreamId = (stream) =>
  stream?.streamSessionId ?? stream?.stream_session_id ?? null;

const getImage = (stream) =>
  stream?.thumbnailUrl || stream?.thumbnail_url || null;

const getLiveUrl = (stream) =>
  stream?.ivsPlaybackUrl || stream?.ivs_playback_url || "";

const getReplayUrl = (stream) =>
  stream?.vodHlsUrl ||
  stream?.vod_hls_url ||
  stream?.vodMp4Url ||
  stream?.vod_mp4_url ||
  "";

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.message || "Unable to load channel streams.";

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const getBadgeLabel = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "Live";
  if (status === "scheduled") return "Scheduled";
  if (["ready", "replay_ready"].includes(status)) return "Replay";
  if (status === "ended") return "Ended";
  if (status === "recording_ready") return "Recording";
  if (status === "processing") return "Processing";
  if (status === "failed") return "Failed";

  return "Stream";
};

const getBadgeVariant = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "live";
  if (status === "scheduled") return "scheduled";
  if (["ready", "replay_ready"].includes(status)) return "replay";
  if (status === "recording_ready") return "recording";
  if (["ended", "processing", "failed"].includes(status)) {
    return status;
  }

  return "default";
};

const getDetailText = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "Streaming now";

  if (status === "scheduled") {
    const date = formatDate(
      stream?.scheduledStart ?? stream?.scheduled_start,
    );

    return date ? `Scheduled ${date}` : "Scheduled";
  }

  if (["ready", "replay_ready"].includes(status)) {
    return (
      formatDate(
        stream?.actualEnd ??
          stream?.actual_end ??
          stream?.createdAt ??
          stream?.created_at,
      ) || "Replay available"
    );
  }

  if (status === "ended") return "Stream ended";
  if (status === "recording_ready") return "Recording ready";
  if (status === "processing") return "Preparing replay";
  if (status === "failed") return "Stream unavailable";

  return "View stream details";
};

export default function ChannelStreamsView({
  username,
  limit = DEFAULT_LIMIT,
  preview = false,
  onContentLoaded,
}) {
  const pageLimit = preview
    ? PREVIEW_LIMIT
    : Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const {
    streams,
    loading,
    loaded,
    loadingMore,
    refreshing,
    hasMore,
    error,
    refresh,
    loadMore,
    selectedStream,
    watchData,
    watchLoading,
    watchError,
    openStream,
    closeStream,
  } = useChannelStreams(username, pageLimit);

  const streamList = Array.isArray(streams) ? streams : [];

  const visibleStreams = preview
    ? streamList.slice(0, PREVIEW_LIMIT)
    : streamList;

  const validStreams = visibleStreams.filter(
    (stream) => getStreamId(stream) != null,
  );

  const hasStreams = validStreams.length > 0;
  const isEmpty = loaded && !loading && !hasStreams;
  const isBusy = loading || loadingMore || refreshing;

  const effectiveStream = watchData || selectedStream;
  const playbackStatus = getStatus(effectiveStream);

  const liveUrl = getLiveUrl(watchData);
  const replayUrl = getReplayUrl(watchData);

  const isReplayOpen = Boolean(
    selectedStream &&
      !watchLoading &&
      !watchError &&
      playbackStatus !== "live" &&
      replayUrl,
  );

  const isInfoOpen = Boolean(selectedStream && !isReplayOpen);

  useEffect(() => {
    if (!preview || !loaded || loading) return;

    onContentLoaded?.({
      hasContent: hasStreams,
      error: Boolean(error),
    });
  }, [preview, loaded, loading, hasStreams, error, onContentLoaded]);

  useEffect(() => {
    if (!isInfoOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeStream();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isInfoOpen, closeStream]);

  const getModalDescription = () => {
    if (playbackStatus === "scheduled") {
      const date = formatDate(
        effectiveStream?.scheduledStart ??
          effectiveStream?.scheduled_start,
      );

      return date
        ? `Scheduled for ${date}`
        : "This stream will be available when broadcasting starts.";
    }

    if (playbackStatus === "ended") {
      return "The stream has ended. A replay is not available yet.";
    }

    if (["recording_ready", "processing"].includes(playbackStatus)) {
      return "The replay is being prepared.";
    }

    if (playbackStatus === "failed") {
      return "This stream is currently unavailable.";
    }

    if (playbackStatus === "live" && !liveUrl && !watchLoading) {
      return "Live playback is currently unavailable.";
    }

    return "This stream is not available yet.";
  };

  const renderStreamModal = () => {
    if (!isInfoOpen || typeof document === "undefined") {
      return null;
    }

    return createPortal(
      <div
        className="channel-stream-modal"
        role="dialog"
        aria-modal="true"
        aria-label={effectiveStream?.title || "Channel stream"}
      >
        <div
          className="channel-stream-modal__backdrop"
          onClick={closeStream}
        />

        <div className="channel-stream-modal__content">
          <button
            type="button"
            className="channel-stream-modal__close"
            onClick={closeStream}
            aria-label="Close stream"
          >
            <X size={19} />
          </button>

          {watchLoading ? (
            <div
              className="channel-stream-modal__message"
              role="status"
            >
              <RefreshCw
                size={25}
                className="channel-stream-modal__spinner"
              />

              <Heading as="h3" size="sm">
                Loading stream...
              </Heading>
            </div>
          ) : watchError ? (
            <div
              className="channel-stream-modal__message"
              role="alert"
            >
              <AlertCircle size={27} />

              <Heading as="h3" size="sm">
                Unable to open stream
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                {getErrorMessage(watchError)}
              </Text>
            </div>
          ) : playbackStatus === "live" && liveUrl ? (
            <div className="channel-stream-modal__player">
              <AmazonIVSPlayer
                key={String(getStreamId(effectiveStream))}
                playbackUrl={liveUrl}
                autoPlay
                muted={false}
                controls
              />
            </div>
          ) : (
            <div className="channel-stream-modal__message">
              <Radio size={27} />

              <Heading as="h3" size="sm">
                {getBadgeLabel(effectiveStream)}
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                {getModalDescription()}
              </Text>
            </div>
          )}

          <div className="channel-stream-modal__details">
            <Heading as="h2" size="md">
              {effectiveStream?.title || "Channel stream"}
            </Heading>

            {(effectiveStream?.description ||
              effectiveStream?.brandName) && (
              <Text as="p" tone="secondary" size="sm">
                {effectiveStream.description ||
                  effectiveStream.brandName}
              </Text>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const renderStream = (stream) => {
    const status = getStatus(stream);

    const canPlay = ["live", "ready", "replay_ready"].includes(
      status,
    );

    return (
      <StreamThumbnailCard
        key={String(getStreamId(stream))}
        stream={stream}
        image={getImage(stream)}
        badgeLabel={getBadgeLabel(stream)}
        badgeVariant={getBadgeVariant(stream)}
        detailText={getDetailText(stream)}
        isLive={status === "live"}
        canPlay={canPlay}
        showPlayButton={canPlay}
        showMenuButton={false}
        menuOptions={[]}
        onClick={openStream}
        isOpen={false}
        onClose={closeStream}
      />
    );
  };

  const renderContent = () => {
    if (loading && !hasStreams) {
      return (
        <ContentGrid
          items={[]}
          loading
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={preview ? PREVIEW_LIMIT : 8}
        />
      );
    }

    if (isEmpty) {
      if (preview) return null;

      return (
        <EmptyState
          icon={<Radio size={28} />}
          title={
            error ? "Unable to load streams" : "No streams available"
          }
          description={
            error
              ? getErrorMessage(error)
              : "Live streams and replays will appear here."
          }
          action={
            error ? (
              <Button
                value="Try Again"
                variant="primary-outlined"
                disabled={isBusy}
                onClick={refresh}
              />
            ) : null
          }
        />
      );
    }

    return (
      <ContentGrid
        items={validStreams}
        columns={4}
        tabletColumns={3}
        mobileColumns={2}
        gap={16}
        showEmpty={false}
        renderItem={renderStream}
      />
    );
  };

  if (preview && isEmpty) return null;

  return (
    <ViewContainer>
      <ViewHeader
        title={`Streams (${visibleStreams.length})`}
        description="Live streams and replays from this channel."
      />

      {error && hasStreams && (
        <div className="channel-stream-view__alert" role="alert">
          <Text as="p" size="sm">
            {getErrorMessage(error)}
          </Text>
        </div>
      )}

      <div className="channel-stream-view__content">
        {renderContent()}
      </div>

      {!preview && hasMore && hasStreams && (
        <div className="channel-stream-view__footer">
          <Button
            value={loadingMore ? "Loading..." : "Load More"}
            variant="primary-outlined"
            disabled={isBusy}
            onClick={loadMore}
          />
        </div>
      )}

      {renderStreamModal()}

      <SimplePlayer
        openModal={isReplayOpen}
        closeModal={closeStream}
        videoUrl={isReplayOpen ? replayUrl : null}
        thumbnail={getImage(effectiveStream) || ""}
        title={effectiveStream?.title || "Stream replay"}
        videoId={getStreamId(effectiveStream)}
        shouldTrack={false}
      />
    </ViewContainer>
  );
}
